/**
 * Frontend gating profiles: one declarative row per user profile, replacing
 * `isValidatedX` conditionals scattered across the views.
 *
 * Every flag here is read by at least one call site, so changing a value changes the
 * UI. Rules enforced elsewhere deliberately have no flag — squad membership, advisor
 * requests and grower creation live in the backend allowlists, and field access in
 * route gating and node scoping. A flag that enforced nothing would invite someone to
 * flip it expecting a behaviour change, or to drop the real guard.
 */

export type NavbarTabKey =
    | 'fields'
    | 'helpCenter'
    | 'home'
    | 'growers'
    | 'products'
    | 'utilities'
    | 'recommendations'
    | 'reports'
    | 'rvs';

export const USER_TYPES = ['rv', 'advisor', 'consultant', 'squad_coach', 'other', 'grower'] as const;
export type UserType = (typeof USER_TYPES)[number];

export interface ProfileCapabilities {
    /** Navbar tabs, in display order. */
    navTabs: NavbarTabKey[];
    /** Register growers, and assign fields to them. */
    canManageGrowers: boolean;
    /**
     * Author visit reports: create them, edit them, and see their own drafts.
     * Profiles without it can still read saved reports.
     */
    canCreateVisitReports: boolean;
    /** Show the floating "Consulta a un experto" shortcut. */
    canAskAnExpert: boolean;
}

export const PROFILES: Record<UserType, ProfileCapabilities> = {
    rv: {
        navTabs: ['home', 'growers', 'fields', 'products', 'utilities', 'helpCenter'],
        canManageGrowers: true,
        canCreateVisitReports: true,
        canAskAnExpert: true,
    },
    advisor: {
        navTabs: ['home', 'growers', 'fields', 'products', 'utilities', 'helpCenter'],
        canManageGrowers: true,
        canCreateVisitReports: true,
        canAskAnExpert: true,
    },
    consultant: {
        navTabs: ['home', 'rvs', 'fields', 'products', 'utilities', 'helpCenter'],
        canManageGrowers: true,
        canCreateVisitReports: true,
        canAskAnExpert: false,
    },
    squad_coach: {
        navTabs: ['home', 'products', 'utilities', 'helpCenter'],
        canManageGrowers: false,
        canCreateVisitReports: false,
        canAskAnExpert: true,
    },
    // Generic / commercial internal profile: own fields only.
    other: {
        navTabs: ['home', 'fields', 'products', 'utilities', 'helpCenter'],
        canManageGrowers: false,
        canCreateVisitReports: true,
        canAskAnExpert: false,
    },
    grower: {
        navTabs: ['home', 'reports', 'recommendations', 'utilities', 'products'],
        canManageGrowers: false,
        canCreateVisitReports: false,
        canAskAnExpert: true,
    },
};
